import * as React from 'react';

import { FieldFormGroup } from '@shared/components/fields/field_form_group';
import { InputFormControl } from '@shared/components/fields/input_form_control';

import { Panel } from '@admin/components/helpers/panel';
import { vehicleTypesURL } from '@admin/config/routes';
import { VehicleTypeInput } from '@admin/schema';
import { getZipDifferenceCount, parseZips } from '@admin/utils/zip_cods';

import { ZipInput } from '../edit_zip_input';

const DEFAULT_VEHICLE_TYPE: VehicleTypeInput = {
  name: '',
  numSeats: 0,
  cuft: 0,
  weightInPounds: 0,
  fieldDispatchable: true,
  restrictedZipCodes: [],
  lengthInInches: 0,
  widthInInches: 0,
  heightInInches: 0,
};

export const VehicleTypeForm: React.FC<{
  existingVehicleType?: VehicleTypeInput;
  onSubmit(vehicleType: VehicleTypeInput): void;
}> = ({ existingVehicleType, onSubmit }) => {
  const [vehicleType, setVehicleType] = React.useState<VehicleTypeInput>(existingVehicleType || DEFAULT_VEHICLE_TYPE);

  const originalRestrictedZipCodesText = vehicleType.restrictedZipCodes.join('\n');
  const [restrictedZipCodesText, setRestrictedZipCodesText] = React.useState<string>(originalRestrictedZipCodesText);

  const restrictedZipCodesSet = new Set(parseZips(restrictedZipCodesText));
  const originalRestrictedZipCodesSet = new Set(parseZips(originalRestrictedZipCodesText));

  const addedRestrictedZipCodesCount = getZipDifferenceCount(restrictedZipCodesSet, originalRestrictedZipCodesSet);
  const removedRestrictedZipCodesCount = getZipDifferenceCount(originalRestrictedZipCodesSet, restrictedZipCodesSet);

  return (
    <>
      <Panel>
        <Panel.Header>
          <Panel.Title>{existingVehicleType ? 'Edit Vehicle Type' : 'New Vehicle Type'}</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <FieldFormGroup label="Name:">
            <InputFormControl
              type="text"
              id="vehicle_type_name"
              placeholder="A"
              value={vehicleType?.name || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, name: e.target.value })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Number of seats:">
            <InputFormControl
              type="number"
              id="vehicle_type_num_seats"
              placeholder="3"
              value={vehicleType?.numSeats || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, numSeats: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Cuft:">
            <InputFormControl
              type="number"
              id="vehicle_type_cuft"
              placeholder="100"
              value={vehicleType?.cuft || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, cuft: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Weight in pounds:">
            <InputFormControl
              type="number"
              id="vehicle_type_weight"
              placeholder="100"
              value={vehicleType?.weightInPounds || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, weightInPounds: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Length in inches:">
            <InputFormControl
              type="number"
              id="vehicle_type_length"
              placeholder="120"
              value={vehicleType?.lengthInInches || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, lengthInInches: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Width in inches:">
            <InputFormControl
              type="number"
              id="vehicle_type_width"
              placeholder="80"
              value={vehicleType?.widthInInches || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, widthInInches: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Height in inches:">
            <InputFormControl
              type="number"
              id="vehicle_type_height"
              placeholder="90"
              value={vehicleType?.heightInInches || ''}
              onChange={(e) => setVehicleType({ ...vehicleType, heightInInches: e.target.valueAsNumber })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Field Dispatchable:">
            <InputFormControl
              type="checkbox"
              id="fieldDispatchable"
              checked={vehicleType.fieldDispatchable}
              onChange={(e) => setVehicleType({ ...vehicleType, fieldDispatchable: e.target.checked })}
            />
          </FieldFormGroup>

          <FieldFormGroup label="Restricted Zip Codes:">
            <ZipInput
              label=""
              value={restrictedZipCodesText}
              addedZipsCount={addedRestrictedZipCodesCount}
              removedZipsCount={removedRestrictedZipCodesCount}
              disabled={false}
              onChange={(e) => {
                setRestrictedZipCodesText(e.target.value);
              }}
            />
          </FieldFormGroup>
        </Panel.Body>
        <Panel.Footer align="right">
          <a href={vehicleTypesURL()} className="btn btn-default">
            Cancel
          </a>
          <button
            type="submit"
            className="btn btn-primary"
            onClick={() => {
              onSubmit({ ...vehicleType!, restrictedZipCodes: Array.from(new Set(parseZips(restrictedZipCodesText))) });
            }}
            disabled={
              !(
                !!vehicleType?.cuft &&
                !!vehicleType?.numSeats &&
                !!vehicleType?.name &&
                !!vehicleType?.weightInPounds &&
                !!vehicleType?.lengthInInches &&
                !!vehicleType?.widthInInches &&
                !!vehicleType?.heightInInches
              )
            }
          >
            Save
          </button>
        </Panel.Footer>
      </Panel>
    </>
  );
};
